import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Rating,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { AcUnit, Close, Compare } from '@mui/icons-material';
import {
  GETProducts,
  GETProductById,
  ProductDetail,
  ProductListItem,
} from '../../services/product';
import { debounce, capitalizeWords } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';

const MAX_PRODUCTS = 4;

type SpecValue = string | number | boolean | string[] | null | undefined;

interface SpecRow {
  label: string;
  get: (p: ProductDetail) => SpecValue;
}

interface SpecSection {
  title: string;
  rows: SpecRow[];
}

const SPEC_SECTIONS: SpecSection[] = [
  {
    title: 'Pricing',
    rows: [
      { label: 'MRP', get: (p) => p.pricing?.mrp },
      { label: 'Customer Price', get: (p) => p.pricing?.customerPrice },
      { label: 'Discount %', get: (p) => p.pricing?.discountedPercentage },
    ],
  },
  {
    title: 'General',
    rows: [
      { label: 'AC Type', get: (p) => p.specifications?.acType },
      { label: 'Tonnage', get: (p) => p.specifications?.tonnage },
      { label: 'Inverter', get: (p) => p.specifications?.inverter },
      { label: 'Star Rating', get: (p) => p.specifications?.starRating },
      { label: 'Year of Mfg', get: (p) => p.yearOfMfg },
      { label: 'Country of Origin', get: (p) => p.countryOfOrigin },
    ],
  },
  {
    title: 'Energy Efficiency',
    rows: [
      { label: 'ISEER', get: (p) => p.specifications?.iseer },
      {
        label: 'Power Consumption',
        get: (p) => p.specifications?.powerConsumption,
      },
      {
        label: 'Power Requirement',
        get: (p) => p.specifications?.powerRequirement,
      },
      { label: 'Power Supply', get: (p) => p.specifications?.powerSupply },
      { label: 'Rated Current', get: (p) => p.specifications?.ratedCurrent },
    ],
  },
  {
    title: 'Cooling Performance',
    rows: [
      {
        label: 'Cooling Capacity',
        get: (p) => p.specifications?.coolingCapacity,
      },
      {
        label: 'Compressor Type',
        get: (p) => p.specifications?.compressorType,
      },
      { label: 'Refrigerant', get: (p) => p.specifications?.refrigerant },
      {
        label: 'Ambient Temperature',
        get: (p) => p.specifications?.ambientTemperature,
      },
      {
        label: 'Convertible Mode',
        get: (p) => p.specifications?.convertibleMode,
      },
      { label: 'Condenser Coil', get: (p) => p.specifications?.condenserCoil },
      { label: 'Coverage Area', get: (p) => p.specifications?.coverageArea },
    ],
  },
  {
    title: 'Air Flow & Filtration',
    rows: [
      {
        label: 'Air Flow Direction',
        get: (p) => p.specifications?.airFlowDirection,
      },
      {
        label: 'Air Flow Volume',
        get: (p) => p.specifications?.airFlowVolume,
      },
      { label: 'Dust Filter', get: (p) => p.specifications?.dustFilter },
      { label: 'Anti Bacteria', get: (p) => p.specifications?.antiBacteria },
    ],
  },
  {
    title: 'Dimensions & Build',
    rows: [
      {
        label: 'Indoor Unit Dimensions',
        get: (p) => p.specifications?.indoorUnitDimensions,
      },
      {
        label: 'Outdoor Unit Dimensions',
        get: (p) => p.specifications?.outdoorUnitDimensions,
      },
      {
        label: 'Indoor Unit Weight',
        get: (p) => p.specifications?.indoorUnitWeight,
      },
      {
        label: 'Outdoor Unit Weight',
        get: (p) => p.specifications?.outdoorUnitWeight,
      },
      { label: 'Body Material', get: (p) => p.specifications?.bodyMaterial },
      { label: 'Color', get: (p) => p.specifications?.color },
    ],
  },
  {
    title: 'Smart Features & Controls',
    rows: [
      { label: 'Remote Control', get: (p) => p.specifications?.remoteControl },
      { label: 'Sleep Mode', get: (p) => p.specifications?.sleepMode },
      { label: 'Auto Restart', get: (p) => p.specifications?.autoRestart },
      {
        label: 'Self Diagnosis',
        get: (p) => p.specifications?.selfDiagnosis,
      },
      { label: 'Timer', get: (p) => p.specifications?.timer },
      { label: 'Display', get: (p) => p.specifications?.display },
      {
        label: 'WiFi Connectivity',
        get: (p) => p.specifications?.wifiConnectivity,
      },
      {
        label: 'Special Features',
        get: (p) => p.specifications?.specialFeatures,
      },
    ],
  },
  {
    title: 'Noise',
    rows: [
      { label: 'Noise Level', get: (p) => p.specifications?.noiseLevel },
      { label: 'Quiet Mode', get: (p) => p.specifications?.quietMode },
    ],
  },
  {
    title: 'Warranty',
    rows: [
      {
        label: 'Product Warranty',
        get: (p) => p.specifications?.warranty?.product,
      },
      {
        label: 'Compressor Warranty',
        get: (p) => p.specifications?.warranty?.compressor,
      },
      { label: 'PCB Warranty', get: (p) => p.specifications?.warranty?.pcb },
    ],
  },
];

const formatValue = (row: SpecRow, value: SpecValue): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'number') {
    if (row.label === 'MRP' || row.label === 'Customer Price')
      return `₹${value.toLocaleString('en-IN')}`;
    if (row.label === 'Discount %') return `${value}%`;
    return String(value);
  }
  return String(value);
};

const Comparison: React.FC = () => {
  const [products, setProducts] = useState<ProductDetail[]>([]);
  const [options, setOptions] = useState<ProductListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [onlyDifferences, setOnlyDifferences] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const searchProducts = useMemo(
    () =>
      debounce(async (search: string) => {
        try {
          setSearching(true);
          const data = await GETProducts({ search, category: 'AC', limit: 10 });
          setOptions(data);
        } catch {
          setOptions([]);
        } finally {
          setSearching(false);
        }
      }, 400),
    [],
  );

  const handleAddProduct = async (option: ProductListItem | null) => {
    if (!option) return;
    if (products.some((p) => p._id === option._id)) {
      showSnackbar('Product is already in the comparison', 'error');
      return;
    }
    if (products.length >= MAX_PRODUCTS) return;

    try {
      setAdding(true);
      const detail = await GETProductById(option._id);
      setProducts((prev) => [...prev, detail]);
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to load product',
        'error',
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = (id: string) => {
    setProducts((prev) => prev.filter((p) => p._id !== id));
  };

  const visibleSections = useMemo(() => {
    if (products.length === 0) return [];

    return SPEC_SECTIONS.map((section) => ({
      title: section.title,
      rows: section.rows.filter((row) => {
        const values = products.map((p) => formatValue(row, row.get(p)));
        const hasAnyValue = values.some((v) => v !== '—');
        if (!hasAnyValue) return false;
        if (onlyDifferences && products.length > 1) {
          return new Set(values).size > 1;
        }
        return true;
      }),
    })).filter((section) => section.rows.length > 0);
  }, [products, onlyDifferences]);

  const labelColWidth = 220;
  const productColWidth = 240;

  return (
    <Box sx={{ m: 3 }}>
      <Box sx={{ backgroundColor: '#1C1C1C', color: '#fff', borderRadius: 2 }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Compare sx={{ color: '#E31E25' }} />
                <Typography variant="h6">AC Comparison</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={onlyDifferences}
                      onChange={(e) => setOnlyDifferences(e.target.checked)}
                      disabled={products.length < 2}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#E31E25',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                          { backgroundColor: '#E31E25' },
                      }}
                    />
                  }
                  label="Show only differences"
                />

                <Autocomplete
                  size="small"
                  sx={{
                    width: 320,
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: '#565656' },
                      '&:hover fieldset': { borderColor: '#9e9e9e' },
                      '&.Mui-focused fieldset': { borderColor: '#E31E25' },
                    },
                    '& .MuiInputLabel-root': { color: '#9e9e9e' },
                    '& .MuiSvgIcon-root': { color: '#9e9e9e' },
                  }}
                  options={options}
                  loading={searching || adding}
                  disabled={products.length >= MAX_PRODUCTS}
                  getOptionLabel={(option) =>
                    [option.name, option.brand, option.model]
                      .filter(Boolean)
                      .join(' · ')
                  }
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  filterOptions={(x) => x}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') searchProducts(value);
                  }}
                  onChange={(_, value) => handleAddProduct(value)}
                  value={null}
                  blurOnSelect
                  clearOnBlur
                  noOptionsText="No AC products found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        products.length >= MAX_PRODUCTS
                          ? `Maximum ${MAX_PRODUCTS} products`
                          : 'Add AC to compare'
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(searching || adding) && (
                              <CircularProgress
                                size={16}
                                sx={{ color: '#E31E25' }}
                              />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          }
        />
        <Divider sx={{ borderColor: '#565656' }} />

        <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
          {products.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#2E2E2E',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
              }}
            >
              <AcUnit sx={{ color: '#565656', fontSize: 48, mb: 1 }} />
              <Typography sx={{ color: '#9e9e9e' }}>
                Search and add up to {MAX_PRODUCTS} AC products to compare
                them side by side.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              {/* Product header cards */}
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Box sx={{ minWidth: labelColWidth, flexShrink: 0 }} />
                {products.map((product) => (
                  <Box
                    key={product._id}
                    sx={{ minWidth: productColWidth, flexShrink: 0, px: 1 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: '#2E2E2E',
                        borderRadius: 2,
                        p: 2,
                        position: 'relative',
                        textAlign: 'center',
                        height: '100%',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(product._id)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          color: '#9e9e9e',
                        }}
                        aria-label="Remove product"
                      >
                        <Close fontSize="small" />
                      </IconButton>

                      {product.images?.[0] ? (
                        <Box
                          component="img"
                          src={product.images[0]}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: 110,
                            objectFit: 'contain',
                            mb: 1,
                          }}
                        />
                      ) : (
                        <AcUnit
                          sx={{ color: '#565656', fontSize: 64, mb: 1 }}
                        />
                      )}

                      <Typography
                        variant="body2"
                        sx={{ color: '#fff', fontWeight: 600 }}
                      >
                        {product.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                        {[product.brand, product.model]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>

                      {typeof product.specifications?.starRating ===
                        'number' && (
                        <Box sx={{ mt: 0.5 }}>
                          <Rating
                            value={product.specifications.starRating}
                            readOnly
                            size="small"
                          />
                        </Box>
                      )}

                      {product.pricing?.customerPrice !== undefined && (
                        <Typography
                          sx={{
                            color: '#0BE600',
                            fontWeight: 700,
                            mt: 0.5,
                          }}
                        >
                          ₹
                          {product.pricing.customerPrice.toLocaleString(
                            'en-IN',
                          )}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                ))}
              </Box>

              {products.length < 2 && (
                <Typography
                  variant="body2"
                  sx={{ color: '#9e9e9e', mb: 2, textAlign: 'center' }}
                >
                  Add at least one more product to see the comparison.
                </Typography>
              )}

              {/* Spec sections */}
              {products.length >= 2 &&
                visibleSections.map((section) => (
                  <Box key={section.title} sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: '#E31E25', fontWeight: 700, mb: 1 }}
                    >
                      {section.title}
                    </Typography>

                    {section.rows.map((row, rowIndex) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: 'flex',
                          backgroundColor:
                            rowIndex % 2 === 0 ? '#2E2E2E' : 'transparent',
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: labelColWidth,
                            flexShrink: 0,
                            px: 2,
                            py: 1.25,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: '#9e9e9e' }}
                          >
                            {row.label}
                          </Typography>
                        </Box>

                        {products.map((product) => (
                          <Box
                            key={product._id}
                            sx={{
                              minWidth: productColWidth,
                              flexShrink: 0,
                              px: 2,
                              py: 1.25,
                              textAlign: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: '#fff' }}
                            >
                              {row.label === 'AC Type'
                                ? capitalizeWords(
                                    formatValue(
                                      row,
                                      row.get(product),
                                    ).replaceAll('_', ' '),
                                  )
                                : formatValue(row, row.get(product))}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                ))}

              {products.length >= 2 &&
                visibleSections.length === 0 &&
                onlyDifferences && (
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#2E2E2E',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                    }}
                  >
                    <Typography sx={{ color: '#9e9e9e' }}>
                      These products have identical specifications for every
                      recorded field.
                    </Typography>
                  </Paper>
                )}
            </Box>
          )}
        </CardContent>
        <Divider sx={{ borderColor: '#565656' }} />
      </Box>

      {SnackbarComponent}
    </Box>
  );
};

export default Comparison;
